import {
  BlobStorageAuthMethod,
  BlobStorageSubtype,
  MutationBlobStorageIntegrationCreateArgs,
  MutationBlobStorageIntegrationUpdateArgs,
} from "types/generated";
import { isSaasDistribution } from "utils/distribution";
import { ROOT_SPACE_ID } from "constants/space";

import { ManageBlobStorageFormFields } from "./types";
import { VCS_INTEGRATION_TYPES } from "../../constants";
import { BlobStorageIntegrationData } from "./Edit/gql";

// The AWS S3 subtype is only available on SaaS; the backend rejects it on self-hosted.
const isAwsS3SubtypeAvailable = isSaasDistribution();

export const BLOB_STORAGE_SUBTYPE_LABELS: Record<BlobStorageSubtype, string> = {
  [BlobStorageSubtype.AwsS3]: "AWS S3",
  [BlobStorageSubtype.S3Compatible]: "S3-compatible (Ceph, MinIO)",
};

export const getBlobStorageSubtypeOptions = () => [
  ...(isAwsS3SubtypeAvailable
    ? [
        {
          value: BlobStorageSubtype.AwsS3,
          label: BLOB_STORAGE_SUBTYPE_LABELS[BlobStorageSubtype.AwsS3],
        },
      ]
    : []),
  {
    value: BlobStorageSubtype.S3Compatible,
    label: BLOB_STORAGE_SUBTYPE_LABELS[BlobStorageSubtype.S3Compatible],
  },
];

// An empty role ARN (AWS S3) or access key ID (S3-compatible) means an anonymous read.
export const deriveBlobStorageAuthMethod = (
  values: Pick<ManageBlobStorageFormFields, "subtype" | "roleArn" | "accessKeyId">
): BlobStorageAuthMethod => {
  if (values.subtype === BlobStorageSubtype.AwsS3) {
    return values.roleArn.trim() ? BlobStorageAuthMethod.AwsRole : BlobStorageAuthMethod.Anonymous;
  }

  return values.accessKeyId.trim()
    ? BlobStorageAuthMethod.AccessKeys
    : BlobStorageAuthMethod.Anonymous;
};

export const getFormDefaultValues = (
  integration?: BlobStorageIntegrationData
): ManageBlobStorageFormFields => ({
  name: integration?.name || "",
  space: integration?.space?.id || "",
  labels: integration?.labels?.map((label) => ({ value: label })) || [],
  description: integration?.description || "",
  integrationType: integration?.isDefault
    ? VCS_INTEGRATION_TYPES.DEFAULT
    : VCS_INTEGRATION_TYPES.MULTIPLE,
  subtype:
    integration?.subtype ??
    (isAwsS3SubtypeAvailable ? BlobStorageSubtype.AwsS3 : BlobStorageSubtype.S3Compatible),
  baseURI: integration?.baseURI || "",
  region: integration?.region || "",
  roleArn: integration?.roleArn || "",
  endpoint: integration?.endpoint || "",
  accessKeyId: integration?.accessKeyId || "",
  // The secret access key is write-only and never returned by the API.
  secretAccessKey: "",
});

export const mapCreateBlobStorageIntegrationInputValues = (
  values: ManageBlobStorageFormFields
): MutationBlobStorageIntegrationCreateArgs => {
  const isDefault = values.integrationType === VCS_INTEGRATION_TYPES.DEFAULT;
  const isAwsS3 = values.subtype === BlobStorageSubtype.AwsS3;

  return {
    subtype: values.subtype,
    authMethod: deriveBlobStorageAuthMethod(values),
    baseURI: values.baseURI.trim(),
    region: isAwsS3 ? values.region.trim() || null : null,
    endpoint: isAwsS3 ? null : values.endpoint.trim() || null,
    roleArn: isAwsS3 ? values.roleArn.trim() || null : null,
    accessKeyId: isAwsS3 ? null : values.accessKeyId.trim() || null,
    secretAccessKey:
      !isAwsS3 && values.accessKeyId.trim() ? values.secretAccessKey.trim() || null : null,
    customInput: {
      isDefault,
      name: values.name.trim(),
      labels: values.labels.map((label) => label.value) || [],
      // FYI: set the root to be able to convert to the multiple integration from the default one
      spaceID: isDefault ? ROOT_SPACE_ID : values.space,
      description: values.description?.trim() || null,
      // Blob Storage has no git checkout or VCS checks; the backend defaults these.
      useGitCheckout: null,
      vcsChecks: null,
      vcsCheckOptions: null,
    },
  };
};

export const mapUpdateBlobStorageIntegrationInputValues = (
  values: ManageBlobStorageFormFields,
  integrationId: string
): MutationBlobStorageIntegrationUpdateArgs => {
  const isAwsS3 = values.subtype === BlobStorageSubtype.AwsS3;

  return {
    subtype: values.subtype,
    authMethod: deriveBlobStorageAuthMethod(values),
    baseURI: values.baseURI.trim(),
    region: isAwsS3 ? values.region.trim() || null : null,
    endpoint: isAwsS3 ? null : values.endpoint.trim() || null,
    roleArn: isAwsS3 ? values.roleArn.trim() || null : null,
    accessKeyId: isAwsS3 ? null : values.accessKeyId.trim() || null,
    // Write-only: empty keeps the stored secret on the backend.
    secretAccessKey: isAwsS3 ? null : values.secretAccessKey.trim() || null,
    customInput: {
      id: integrationId,
      labels: values.labels.map((label) => label.value) || [],
      space: values.space,
      description: values.description?.trim() || null,
      // Blob Storage has no git checkout or VCS checks; the backend keeps these unchanged.
      useGitCheckout: null,
      vcsChecks: null,
      vcsCheckOptions: null,
    },
  };
};
